import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  Flex,
  Spinner,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useToast,
} from "@chakra-ui/react";
import { supabase } from "../../../lib/supabaseClient";

const PAGE_SIZE = 20;
const columns = [
  { key: "isrc", label: "ISRC" },
  { key: "name_album", label: "앨범명" },
  { key: "name_musician", label: "뮤지션명" },
  { key: "name_track", label: "트랙명" },
  { key: "ownership_share", label: "지분" },
  { key: "ownership_user", label: "오너쉽 유저" },
  { key: "created_date", label: "등록일" },
  { key: "actions", label: "관리" },
];

function toIsoDatetimeString(date) {
  if (!date) return null;
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toISOString();
}

const Ownership = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selected, setSelected] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addFields, setAddFields] = useState({
    isrc: "",
    name_album: "",
    name_musician: "",
    name_track: "",
    ownership_label: "",
    ownership_share: "",
    ownership_user: "",
    track_code: "",
  });
  const toast = useToast();
  const [trackInfo, setTrackInfo] = useState(null);
  const [labelInfo, setLabelInfo] = useState(null);
  const [inputPage, setInputPage] = useState(page.toString());

  const fetchData = async () => {
    setLoading(true);
    let query = supabase
      .from("ownership")
      .select("*", { count: "exact" })
      .order("created_date", { ascending: false })
      .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);
    if (search) {
      query = query.or(
        `isrc.ilike.%${search}%,ownership_user.ilike.%${search}%,track_code.ilike.%${search}%,name_track.ilike.%${search}%,name_album.ilike.%${search}%`
      );
    }
    const { data: rows, count, error } = await query;
    if (!error) {
      setData(rows || []);
      setTotalPages(Math.max(1, Math.ceil((count || 1) / PAGE_SIZE)));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [search, page]);

  useEffect(() => {
    if (modalOpen && editFields.track_code) {
      supabase
        .from("track")
        .select("track_code")
        .eq("id", editFields.track_code)
        .single()
        .then(({ data }) => setTrackInfo(data));
    } else {
      setTrackInfo(null);
    }
    if (modalOpen && editFields.ownership_label) {
      supabase
        .from("label")
        .select("name")
        .eq("id", editFields.ownership_label)
        .single()
        .then(({ data }) => setLabelInfo(data));
    } else {
      setLabelInfo(null);
    }
  }, [modalOpen, editFields.track_code, editFields.ownership_label]);

  useEffect(() => {
    setInputPage(String(page));
  }, [page]);

  const handleRowClick = (row) => {
    setSelected(row);
    setEditFields(row);
    setModalOpen(true);
  };

  const handleEditChange = (key, value) => {
    setEditFields((prev) => ({ ...prev, [key]: value }));
  };

  const handleAddChange = (key, value) => {
    setAddFields((prev) => ({ ...prev, [key]: value }));
  };

  // 발매 등록하기 (supabase 수정 + 외부 API POST)
  const handleRegister = async () => {
    if (!selected) return;
    // supabase 수정
    const { error } = await supabase
      .from("ownership")
      .update(editFields)
      .eq("id", selected.id);
    if (error) {
      toast({
        title: "수정 실패",
        status: "error",
        description: error.message,
      });
      return;
    }
    // 토큰 발급
    const { data: userData } = await supabase
      .from("user")
      .select("id")
      .eq("email", editFields.ownership_user)
      .single();
    const userId = userData?.id;
    const tokenRes = await fetch("https://plpl.rhoonart.co.kr/auth/token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId }),
    });
    const tokenJson = await tokenRes.json();
    const access_token = tokenJson.data?.access_token;
    // 외부 API POST
    const body = {
      id: selected.id,
      isrc: editFields.isrc,
      album: editFields.name_album,
      musician: editFields.name_musician,
      trackName: editFields.name_track,
      labelId: editFields.ownership_label,
      trackCodeId: editFields.track_code,
      contractorId: userId,
      shareRatio: Number(editFields.ownership_share) || 0,
      playlist: true,
      copyrights: true,
      laeeblyForShorts: true,
      legal: true,
      melon: 0,
      creatorId: userId,
      createAt: toIsoDatetimeString(new Date()),
      updateAt: toIsoDatetimeString(new Date()),
    };
    await fetch("https://plpl.rhoonart.co.kr/ownerships", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${access_token}`,
      },
      body: JSON.stringify(body),
    });
    toast({ title: "발매 등록 완료", status: "success" });
    setModalOpen(false);
    fetchData();
  };

  // 수정하기 (supabase 수정 + 외부 API PUT)
  const handleUpdate = async () => {
    if (!selected) return;
    // supabase 수정
    const { error } = await supabase
      .from("ownership")
      .update(editFields)
      .eq("id", selected.id);
    if (error) {
      toast({
        title: "수정 실패",
        status: "error",
        description: error.message,
      });
      return;
    }
    // 토큰 발급
    const { data: userData } = await supabase
      .from("user")
      .select("id")
      .eq("email", editFields.ownership_user)
      .single();
    const userId = userData?.id;
    const tokenRes = await fetch("https://plpl.rhoonart.co.kr/auth/token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId }),
    });
    const tokenJson = await tokenRes.json();
    const access_token = tokenJson.data?.access_token;
    // 외부 API PUT
    const body = {
      id: selected.id,
      isrc: editFields.isrc,
      album: editFields.name_album,
      musician: editFields.name_musician,
      trackName: editFields.name_track,
      labelId: editFields.ownership_label,
      trackCodeId: editFields.track_code,
      contractorId: userId,
      shareRatio: Number(editFields.ownership_share) || 0,
      playlist: true,
      copyrights: true,
      laeeblyForShorts: true,
      legal: true,
      melon: 0,
      creatorId: userId,
      createAt: toIsoDatetimeString(selected.created_date),
      updateAt: toIsoDatetimeString(new Date()),
    };
    await fetch(`https://plpl.rhoonart.co.kr/ownerships/${selected.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${access_token}`,
      },
      body: JSON.stringify(body),
    });
    toast({ title: "수정 완료", status: "success" });
    setModalOpen(false);
    fetchData();
  };

  const handleAddOwnership = async () => {
    // supabase 추가
    const { data: ownershipData, error } = await supabase
      .from("ownership")
      .insert({
        isrc: addFields.isrc,
        ownership_user: addFields.ownership_user,
        ownership_share: Number(addFields.ownership_share) || 0,
        track_code: addFields.track_code,
        name_album: addFields.name_album,
        name_track: addFields.name_track,
        name_musician: addFields.name_musician,
        ownership_label: addFields.ownership_label,
      })
      .select()
      .single();
    if (error) {
      toast({
        title: "추가 실패",
        status: "error",
        description: error.message,
      });
      return;
    }
    // 토큰 발급
    const { data: userData } = await supabase
      .from("user")
      .select("id")
      .eq("email", addFields.ownership_user)
      .single();
    const userId = userData?.id;
    const tokenRes = await fetch("https://plpl.rhoonart.co.kr/auth/token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId }),
    });
    const tokenJson = await tokenRes.json();
    const access_token = tokenJson.data?.access_token;
    // 외부 API POST
    const body = {
      id: ownershipData.id,
      isrc: addFields.isrc,
      album: addFields.name_album,
      musician: addFields.name_musician,
      trackName: addFields.name_track,
      labelId: addFields.ownership_label,
      trackCodeId: addFields.track_code,
      contractorId: userId,
      shareRatio: Number(addFields.ownership_share) || 0,
      playlist: true,
      copyrights: true,
      laeeblyForShorts: true,
      legal: true,
      melon: 0,
      creatorId: userId,
      createAt: toIsoDatetimeString(new Date()),
      updateAt: toIsoDatetimeString(new Date()),
    };
    await fetch("https://plpl.rhoonart.co.kr/ownerships", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${access_token}`,
      },
      body: JSON.stringify(body),
    });
    toast({ title: "오너쉽 추가 완료", status: "success" });
    setAddModalOpen(false);
    setAddFields({
      isrc: "",
      name_album: "",
      name_musician: "",
      name_track: "",
      ownership_label: "",
      ownership_share: "",
      ownership_user: "",
      track_code: "",
    });
    fetchData();
  };

  const handleDuplicate = async () => {
    if (!selected) return;

    // 기존 데이터에서 id를 제외한 데이터 복사
    const { id, created_date, ...duplicateData } = editFields;

    // supabase에 새로운 데이터 추가
    const { data: ownershipData, error } = await supabase
      .from("ownership")
      .insert(duplicateData)
      .select()
      .single();

    if (error) {
      toast({
        title: "복제 실패",
        status: "error",
        description: error.message,
      });
      return;
    }

    // 토큰 발급
    const { data: userData } = await supabase
      .from("user")
      .select("id")
      .eq("email", duplicateData.ownership_user)
      .single();
    const userId = userData?.id;
    const tokenRes = await fetch("https://plpl.rhoonart.co.kr/auth/token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId }),
    });
    const tokenJson = await tokenRes.json();
    const access_token = tokenJson.data?.access_token;

    // 외부 API POST
    const body = {
      id: ownershipData.id,
      isrc: duplicateData.isrc,
      album: duplicateData.name_album,
      musician: duplicateData.name_musician,
      trackName: duplicateData.name_track,
      labelId: duplicateData.ownership_label,
      trackCodeId: duplicateData.track_code,
      contractorId: userId,
      shareRatio: Number(duplicateData.ownership_share) || 0,
      playlist: true,
      copyrights: true,
      laeeblyForShorts: true,
      legal: true,
      melon: 0,
      creatorId: userId,
      createAt: toIsoDatetimeString(new Date()),
      updateAt: toIsoDatetimeString(new Date()),
    };

    await fetch("https://plpl.rhoonart.co.kr/ownerships", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${access_token}`,
      },
      body: JSON.stringify(body),
    });

    toast({ title: "오너쉽 복제 완료", status: "success" });
    setModalOpen(false);
    fetchData();
  };

  const handlePageInput = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) setInputPage(value);
  };

  const handlePageGo = () => {
    const num = parseInt(inputPage, 10);
    if (num >= 1 && num <= totalPages) setPage(num);
    else
      toast({ title: "올바른 페이지 번호를 입력하세요.", status: "warning" });
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <Box maxW="1400px" mx="auto" py={8}>
      <Flex mb={4} gap={2} align="center" justify="space-between">
        <Flex gap={2}>
          <Input
            placeholder="ISRC, 오너쉽 유저, 트랙코드, 트랙명, 앨범명 검색"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleKeyPress}
            width="370px"
          />
          <Button colorScheme="blue" onClick={handleSearch}>
            검색
          </Button>
        </Flex>
        <Button colorScheme="teal" onClick={() => setAddModalOpen(true)}>
          오너쉽 추가
        </Button>
      </Flex>
      <Box overflowX="auto" bg="white" borderRadius="xl" boxShadow="md">
        <Table size="sm">
          <Thead bg="gray.50">
            <Tr>
              {columns.map((col) => (
                <Th key={col.key}>{col.label}</Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={columns.length} textAlign="center">
                  <Spinner />
                </Td>
              </Tr>
            ) : data.length === 0 ? (
              <Tr>
                <Td colSpan={columns.length} textAlign="center">
                  <Text color="gray.400">데이터 없음</Text>
                </Td>
              </Tr>
            ) : (
              data.map((row) => (
                <Tr key={row.id}>
                  {columns.map((col) => (
                    <Td
                      key={col.key}
                      maxW="200px"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {col.key === "actions" ? (
                        <Button
                          size="sm"
                          colorScheme="blue"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(row);
                          }}
                        >
                          수정
                        </Button>
                      ) : col.key === "created_date" ? (
                        row[col.key] ? (
                          new Date(row[col.key]).toLocaleDateString()
                        ) : (
                          "-"
                        )
                      ) : (
                        row[col.key] ?? "-"
                      )}
                    </Td>
                  ))}
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>
      {/* 페이지네이션 */}
      <Flex justify="center" mt={4} gap={2} align="center">
        <Button
          size="sm"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          isDisabled={page === 1}
        >
          이전
        </Button>
        <Input
          value={inputPage}
          onChange={handlePageInput}
          onBlur={handlePageGo}
          size="sm"
          width="50px"
          textAlign="center"
          fontSize="sm"
          placeholder="페이지"
        />
        <Button size="sm" onClick={handlePageGo}>
          이동
        </Button>
        <Text fontSize="sm" px={2}>
          / {totalPages}
        </Text>
        <Button
          size="sm"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          isDisabled={page === totalPages}
        >
          다음
        </Button>
      </Flex>
      {/* 수정 모달 */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Flex align="center" gap={3}>
              <Text>오너쉽 정보 수정</Text>
              <Button colorScheme="green" size="sm" onClick={handleDuplicate}>
                복제하기
              </Button>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {/* 나머지 컬럼(등록일 제외) */}
            {columns
              .filter(
                (col) =>
                  col.key !== "created_date" &&
                  col.key !== "ownership_label" &&
                  col.key !== "track_code" &&
                  col.key !== "actions"
              )
              .map((col) => (
                <Flex
                  key={col.key}
                  align="center"
                  mb={3}
                  direction="column"
                  alignItems="flex-start"
                >
                  <Box minW="120px">{col.label}</Box>
                  <Input
                    value={editFields[col.key] ?? ""}
                    onChange={(e) => handleEditChange(col.key, e.target.value)}
                  />
                </Flex>
              ))}
            {/* track_code 입력란과 트랙코드 정보 */}
            <Flex
              align="center"
              mb={3}
              direction="column"
              alignItems="flex-start"
            >
              <Box minW="120px">트랙코드</Box>
              <Input
                value={editFields.track_code ?? ""}
                onChange={(e) => handleEditChange("track_code", e.target.value)}
              />
              <Input
                value={trackInfo?.track_code || "-"}
                isReadOnly
                mt={1}
                bg="gray.50"
                border="1px solid #E2E8F0"
                color="gray.700"
                _focus={{ boxShadow: "none" }}
                _hover={{}}
                _placeholder={{ color: "gray.400" }}
              />
            </Flex>
            {/* ownership_label 입력란과 레이블명 정보 */}
            <Flex
              align="center"
              mb={3}
              direction="column"
              alignItems="flex-start"
            >
              <Box minW="120px">레이블</Box>
              <Input
                value={editFields.ownership_label ?? ""}
                onChange={(e) =>
                  handleEditChange("ownership_label", e.target.value)
                }
              />
              <Input
                value={labelInfo?.name || "-"}
                isReadOnly
                mt={1}
                bg="gray.50"
                border="1px solid #E2E8F0"
                color="gray.700"
                _focus={{ boxShadow: "none" }}
                _hover={{}}
                _placeholder={{ color: "gray.400" }}
              />
            </Flex>
          </ModalBody>
          <ModalFooter justifyContent="space-between">
            <Button colorScheme="teal" onClick={handleRegister}>
              발매 등록하기
            </Button>
            <Flex gap={3}>
              <Button colorScheme="blue" onClick={handleUpdate}>
                수정하기
              </Button>
              <Button onClick={() => setModalOpen(false)}>취소</Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* 오너쉽 추가 모달 */}
      <Modal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        size="2xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>오너쉽 추가</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {[
              "isrc",
              "name_album",
              "name_musician",
              "name_track",
              "ownership_label",
              "ownership_share",
              "ownership_user",
              "track_code",
            ].map((key) => (
              <Flex key={key} align="center" mb={3}>
                <Box minW="120px">
                  {
                    {
                      isrc: "ISRC",
                      name_album: "앨범명",
                      name_musician: "뮤지션명",
                      name_track: "트랙명",
                      ownership_label: "레이블",
                      ownership_share: "지분",
                      ownership_user: "오너쉽 유저",
                      track_code: "트랙코드",
                    }[key]
                  }
                </Box>
                <Input
                  value={addFields[key] ?? ""}
                  onChange={(e) => handleAddChange(key, e.target.value)}
                />
              </Flex>
            ))}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="teal" onClick={handleAddOwnership}>
              등록
            </Button>
            <Button ml={3} onClick={() => setAddModalOpen(false)}>
              취소
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Ownership;
